<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import SalesReviewPageHeader from '../components/SalesReviewPageHeader.vue';
import { useSalesRegistration } from '../composables/useSalesRegistration';
import { isSalesReviewPartyRole } from '../composables/salesReviewPartyGroups';
import {
  salesReviewLocation,
  salesReviewReturnQuery,
} from '../composables/salesReviewNavigation';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const transactionId = computed(() => route.params.salesTransactionId);
const role = computed(() => String(route.params.partyRole || ''));
const partyId = computed(() => route.params.partyId);
const invalid = computed(() => !isSalesReviewPartyRole(role.value));
const unresolved = ref(false);
const registration = useSalesRegistration({
  id: transactionId,
  t,
  notify: useAlert,
});

const goBack = () =>
  router.push(
    salesReviewLocation({
      accountId: route.params.accountId,
      salesTransactionId: transactionId.value,
    })
  );
const resolveParty = transaction => {
  if (invalid.value || !transaction) return;
  const party = (transaction.participants || []).find(
    participant =>
      String(participant.id) === String(partyId.value) &&
      participant.role === role.value
  );
  if (!party) {
    unresolved.value = true;
    return;
  }

  if (party.relation_type === 'representante') {
    if (!party.parent_party_id) {
      unresolved.value = true;
      return;
    }
    router.replace({
      name: 'operacao_imobiliaria_vendas_cadastro_representante',
      params: {
        accountId: route.params.accountId,
        salesTransactionId: transactionId.value,
        partyRole: role.value,
        parentPartyId: party.parent_party_id,
        partyId: party.id,
      },
      query: salesReviewReturnQuery,
    });
    return;
  }

  router.replace({
    name: 'operacao_imobiliaria_vendas_cadastro_parte',
    params: {
      accountId: route.params.accountId,
      salesTransactionId: transactionId.value,
      partyRole: role.value,
      partyKind: String(party.kind || 'pessoa_fisica').replace('_', '-'),
      partyId: party.id,
    },
    query: salesReviewReturnQuery,
  });
};

watch(() => registration.transaction.value, resolveParty, { immediate: true });
</script>

<template>
  <div class="min-h-full bg-lais-bg-soft">
    <SalesReviewPageHeader
      :can-submit="false"
      is-correction
      read-only
      :saving="false"
      :transaction="registration.transaction.value"
      @back="goBack"
    />
    <main class="mx-auto w-full max-w-[1100px] px-4 py-8 sm:px-6">
      <p
        v-if="invalid || unresolved || registration.error.value"
        class="rounded-lais border border-lais-danger bg-red-50 p-4 text-sm text-lais-danger"
      >
        {{ t('SALES_PARTIES.REVIEW.RESOLVE_ERROR') }}
      </p>
      <p
        v-else
        class="rounded-lais border border-lais-border-soft bg-white p-8 text-center text-sm text-lais-text-muted"
      >
        {{ t('SALES.COMMON.LOADING') }}
      </p>
    </main>
  </div>
</template>
